import type { OrderWithPlausibilitySummary } from '@einwert/valuation-api-client'
import { useGetOrdersWithPlausibility } from 'ClientPortal/src/api/Queries/orders'
import { OrderStatus } from 'ComponentLibrary'
import { isEmpty } from 'lodash'
import { FilterState as FilterStateFull } from 'ClientPortal/src/pages/Orders/OrderList/OrderListPageContent'
import { DateRangeSchema } from 'ClientPortal/src/pages/schemas.zod'
import { z } from 'zod'

export type OrderListItem = OrderWithPlausibilitySummary['order'] & Omit<OrderWithPlausibilitySummary, 'order'>

type DateRange = z.infer<typeof DateRangeSchema>
type FilterState = Partial<FilterStateFull>

export function useOrderListPageContent({ filters = {}, defaultStatuses }: { filters: FilterState; defaultStatuses: OrderStatus[] }) {
    const { orders: ordersWithPlausibility, isOrdersPending } = useGetOrdersWithPlausibility({
        ...filters,
        statuses: getStatusFilter(filters, defaultStatuses),
    })

    const orders: OrderListItem[] = ordersWithPlausibility.map(({ order, ...summary }) => ({
        ...order,
        ...summary,
        participant_ids: summary.participant_ids ?? [],
    }))

    return {
        isPending: isOrdersPending,
        orders,
    }
}

function getStatusFilter(filters: FilterState, defaultStatuses: OrderStatus[]): OrderStatus[] | undefined {
    // If user is filtering by any criteria BUT hasn't selected any status filters,
    // return undefined to disable status filtering
    if (hasSomeFilter(filters) && isEmpty(filters.statuses)) {
        return undefined
    }

    if (!isEmpty(filters.statuses)) {
        return filters.statuses
    }

    return defaultStatuses
}

export function hasSomeFilter(filterState: FilterState): boolean {
    return Object.entries(filterState).some(([fieldName, filter]) => {
        if (fieldName === 'valuationDateRange' && filter) {
            const { start, end } = filter as DateRange
            return start !== null || end !== null
        }
        return !isEmpty(filter)
    })
}
